import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  HeadlineM,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import { glossary } from '@sorare/core/src/lib/glossary';

import { RewardPreview } from 'components/missions/Mission/RewardPreview';
import { RewardBoxDialogWithOnboarding } from 'components/rewards/RewardBoxDialogWithOnboarding';

import { MissionTimeLeft } from '../MissionTimeLeft';
import type { MissionTile_managerProgressionMission } from './__generated__/index.graphql';

const Wrapper = styled(Box)`
  position: relative;
  overflow: hidden;
  border: 1px solid #01fff8;
  padding: 0;
  background: linear-gradient(120deg, #01534e -6.77%, #202020 44.68%);
`;

const Header = styled(SBHorizontal)`
  color: #01fff8;
`;

const Content = styled(SBHorizontal)`
  width: 100%;
  align-items: flex-end;
`;

const WithTilesBackground = styled(Vertical)`
  --tile-size: 44px;
  --offset: calc(var(--tile-size) / 2);
  background: linear-gradient(to bottom, transparent, var(--c-black) 80%),
    repeating-linear-gradient(
      to bottom,
      #01fff8 var(--offset),
      transparent calc(var(--offset) + 1px),
      transparent calc(var(--offset) + var(--tile-size))
    ),
    repeating-linear-gradient(
      to right,
      #01fff8 var(--offset),
      transparent calc(var(--offset) + 1px),
      transparent calc(var(--offset) + var(--tile-size))
    ),
    linear-gradient(to bottom, #3ddc8435, #3ddc8400);
  padding: var(--double-unit);
`;

type Props = {
  mission: MissionTile_managerProgressionMission;
  onClaim: () => void;
};
export const MissionTile = ({ mission, onClaim }: Props) => {
  const [rewardDialogOpened, setRewardDialogOpened] = useState(false);

  const { aasmState, probabilisticBundles, configRewards, title, description } =
    mission;
  const completed = aasmState === ManagerProgressionTaskState.COMPLETED;
  const claimed = aasmState === ManagerProgressionTaskState.CLAIMED;
  const probabilisticBundle = probabilisticBundles.at(0);
  const reward = configRewards.at(0);

  return (
    <>
      <Wrapper>
        <WithTilesBackground>
          <Header>
            <LabelS bold>
              <FormattedMessage {...glossary.daily} />
            </LabelS>
            <MissionTimeLeft mission={mission} />
          </Header>
          <Vertical gap={0}>
            <HeadlineM>{title}</HeadlineM>
            <BodyS color="var(--c-nd-600)">{description}</BodyS>
          </Vertical>
          <Content gap={3}>
            {completed || claimed ? (
              <Button
                size="medium"
                color="secondary"
                onClick={onClaim}
                disabled={claimed}
              >
                {claimed ? (
                  <FormattedMessage {...glossary.claimed} />
                ) : (
                  <FormattedMessage {...glossary.claim} />
                )}
              </Button>
            ) : (
              <div />
            )}
            <Vertical center>
              <LabelM bold>
                <FormattedMessage {...glossary.reward} />
              </LabelM>
              {reward && <RewardPreview reward={reward} />}
            </Vertical>
          </Content>
        </WithTilesBackground>
      </Wrapper>
      {probabilisticBundle && rewardDialogOpened && (
        <RewardBoxDialogWithOnboarding
          open
          onClose={() => {
            setRewardDialogOpened(false);
          }}
          bundle={probabilisticBundle}
        />
      )}
    </>
  );
};
MissionTile.fragments = {
  managerProgressionMission: gql`
    fragment MissionTile_managerProgressionMission on ManagerProgressionMission {
      id
      aasmState
      title
      description
      name
      configRewards {
        ...RewardPreview_managerTaskRewardInterface
      }
      probabilisticBundles {
        id
        ...RewardBoxDialogWithOnboarding_probabilisticBundle
      }
      ...MissionTimeLeft_managerProgressionMission
    }
    ${RewardBoxDialogWithOnboarding.fragments.probabilisticBundle}
    ${RewardPreview.fragments.managerTaskRewardInterface}
    ${MissionTimeLeft.fragments.managerProgressionMission}
  ` as TypedDocumentNode<MissionTile_managerProgressionMission>,
};
